import React, { useEffect, useMemo, useRef, useState } from 'react';
import { confirmEmailVerification, checkPasswordResetCode, resetPassword } from '../../services/auth';
import AuthActionStatus, { AuthActionStatusKind } from './AuthActionStatus';
import NewPasswordForm from './NewPasswordForm';

export const AUTH_ACTION_PATH = '/auth/action';

// Landing page for Firebase's emailed auth links (the project's custom action
// URL, which only Firebase support can change for this project — the Console
// and the Identity Toolkit API both refuse it). Firebase appends
// ?mode=verifyEmail|resetPassword&oobCode=...; this applies the code and shows
// the result. Rendered outside AuthGate — the user is often signed out here.
function AuthActionPage() {
    const { mode, oobCode } = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return { mode: params.get('mode'), oobCode: params.get('oobCode') };
    }, []);
    const [status, setStatus] = useState<AuthActionStatusKind>('verifying');
    const [resetEmail, setResetEmail] = useState<string | null>(null);

    // Codes are single-use: ref-guarded so React 18 StrictMode's double-mount
    // (dev only) doesn't spend the code and then report the link as invalid.
    const didRunRef = useRef(false);
    useEffect(() => {
        if (didRunRef.current) return;
        didRunRef.current = true;

        const fail = (err: unknown) => {
            console.error(`Auth action "${mode}" failed:`, err);
            setStatus('invalidLink');
        };

        if (!oobCode) {
            setStatus('invalidLink');
        } else if (mode === 'verifyEmail') {
            confirmEmailVerification(oobCode).then(() => setStatus('emailVerified'), fail);
        } else if (mode === 'resetPassword') {
            checkPasswordResetCode(oobCode).then(setResetEmail, fail);
        } else {
            fail(new Error('Unsupported mode'));
        }
    }, [mode, oobCode]);

    if (resetEmail && oobCode && status === 'verifying') {
        return (
            <NewPasswordForm
                email={resetEmail}
                onSubmit={async (newPassword) => {
                    await resetPassword(oobCode, newPassword);
                    setStatus('passwordChanged');
                }}
            />
        );
    }

    return <AuthActionStatus status={status} />;
}

export default AuthActionPage;
